import api from "@/lib/axiosConfig";
import { useMutation } from "@tanstack/react-query";

export type ArtmatchResult = {
  artworkId: string;
  s3_image_ref: string;
  similarity_score: number;
  title: string;
  year: string;
  author_name?: string;
  author_firstname?: string;
  publication_status: "published" | "draft";
  url: string;
  attachmentPublished: boolean;
};

const baseUrl = "back/api/artmatch";

const uploadArtmatchImage = async (image: File) => {
  const formData = new FormData();
  formData.append("image", image);

  const { data } = await api.post<ArtmatchResult[]>(baseUrl, formData);
  return data;
};

export const useArtmatchResults = () =>
  useMutation({
    mutationFn: uploadArtmatchImage,
    onError: () => {},
  });

/** Only artwork master face photos are indexed by Artmatch. */
export const ARTWORK_FACE_POSITION_FOR_ARTMATCH = "oeuvresPhotoFaceOeuvre";

export function shouldTriggerArtmatchEmbed(
  before: { position: string; published?: boolean },
  after: { position: string; published?: boolean; images?: File[] },
): boolean {
  const position = after.position || before.position;
  if (position !== ARTWORK_FACE_POSITION_FOR_ARTMATCH) {
    return false;
  }
  if (!after.published) {
    return false;
  }
  const publishedChanged = before.published !== after.published;
  const positionChanged = before.position !== after.position;
  const hasNewImage = (after.images?.length ?? 0) > 0;
  return publishedChanged || positionChanged || hasNewImage;
}

/** Runs Artmatch indexing without blocking the save UI. */
export function runArtmatchEmbedInBackground(
  embed: (attachmentId: string) => Promise<unknown>,
  attachmentId: string,
): void {
  void embed(attachmentId).catch((err) => {
    console.error("[artmatch] embed failed", err);
  });
}

const embedAttachment = async (attachmentId: string) => {
  const { data } = await api.post<{
    message: string;
  }>(`${baseUrl}/embed`, { attachmentId });
  return data;
};

export const useEmbedAttachment = () =>
  useMutation({
    mutationFn: embedAttachment,
  });
